import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  TabPanel,
  TabPanels,
  Tabs,
  Tab,
  Spinner,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BoxHeader from "components/elements/BoxHeader";
import Draws from "components/elements/Draws";
import StatementCreation from "components/elements/StatementCreation";
import { FeeTab } from "components/elements/FeeTab/fee-tab";
import DrawTable from "components/loans/DrawTable";
import EditDrawItem from "pages/DrawSchedule/elements/EditDrawItem";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useCallback, useContext, useEffect, useState } from "react";
import _ from "underscore";
import { checkUiConfiguration } from "utils/uiconfighelper";
import { checkDisability, permissionConstants, hasPermission } from "utils/permissionHelper";
import BulletPayemtsTable from "components/elements/BulletPayements";
import DefaultInterestTab from "../DefaultInterestTab";
import { useFeatures } from "hooks/useFeatures";
import InterestHistoryTab from "../InterestHistoryTab";
import { BaseRateTab } from "../BaseRateTab/base-rate-tab";
import { Balance } from "./components/balance";

const PaymentsTab: React.FC<{ updateBudget: (newBudget: number) => void }> = ({ updateBudget }) => {
  const { loan, drawRequest } = useContext(LoanContext);
  const { user } = useTenant();
  const [items, setItems] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [, setCurrentTab] = useState("tab1");
  const [readLoan, setReadLoan] = useState(false);
  const { hasFeature } = useFeatures();
  const showDefaultInterestTab = hasFeature(`default_interest_tab`);

  const readOnlyLoan = useCallback(() => {
    if (hasPermission(user, permissionConstants.readOnlyLoans)) {
      setReadLoan(true);
    }
    if (hasPermission(user, permissionConstants.viewPayments)) {
      setReadLoan(false);
    }
  }, [user]);

  useEffect(() => {
    readOnlyLoan();
  }, [readOnlyLoan]);

  const [sections, setSections] = useState<{
    [key: number]: any[];
  }>({});

  useEffect(() => {
    if (loan) {
      setItems(loan.draw_requests ?? []);
      setPayments(loan.draw_requests?.filter((draw: any) => draw.status === "paid") ?? []);
    }
  }, [loan]);

  useEffect(() => {
    const groupedSections = _.groupBy(items, item => item.section);
    setSections(groupedSections);
    const newBudget = items.reduce((prev, curr) => (prev += curr.amount), 0);
    updateBudget(newBudget);
  }, [items, updateBudget]);

  const addSection = () => {
    const latestSection = Object.keys(sections).length;
    setSections({
      ...sections,
      [latestSection]: [],
    });
  };

  if (!loan) {
    return <Spinner />;
  }

  return (
    <>
      <Flex flexDir="column">
        <Flex p="24px" bg="white" w="100%" borderRadius="20px">
          <Box flex="4">
            <BoxHeader title="Draw Schedule" />
            {user.type === "lender" && (
              <Box>
                <Text fontSize="13px">
                  Add draw items to create the draw schedule for this loan. Each draw item requires
                  a due date and estimated amount due.
                </Text>
                <Button
                  variant="ghost"
                  color="primary"
                  size="sm"
                  pl="0px"
                  mt="10px"
                  mb="20px"
                  isDisabled={readLoan}
                  onClick={addSection}
                  leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
                >
                  New Draw Section
                </Button>
              </Box>
            )}
            <DrawTable sections={sections} />
            {drawRequest && <EditDrawItem />}
          </Box>
          <Balance />
        </Flex>
      </Flex>
      <Tabs onChange={index => setCurrentTab(`tab${index + 1}`)} colorScheme="#00000">
        <HStack
          pt={4}
          borderTopRadius={"20px"}
          background={"white"}
          marginTop={4}
          borderBottom="3px solid #DEDCE2"
        >
          {checkDisability(user, "enebale_payments") && <Tab>Payments</Tab>}

          {checkUiConfiguration(user, "enable_fees") && <Tab>Fees</Tab>}

          <Tab>Interest History</Tab>

          <Tab>Base Rate</Tab>

          {showDefaultInterestTab && <Tab>Default Interest</Tab>}

          <Tab>Statements</Tab>
        </HStack>
        <TabPanels>
          {checkDisability(user, "enebale_payments") && (
            <TabPanel p={6} pt={4} bg="white" borderBottomRadius={20}>
              <BulletPayemtsTable loan={loan!} />
            </TabPanel>
          )}

          {checkUiConfiguration(user, "enable_fees") && (
            <TabPanel p={6} pt={4} bg="white" borderBottomRadius={20}>
              <FeeTab loan={loan!} />
            </TabPanel>
          )}

          <TabPanel p={6} pt={4} bg="white" borderBottomRadius={20}>
            <InterestHistoryTab />
          </TabPanel>

          <TabPanel p={6} pt={4} bg="white" borderBottomRadius={20}>
            <BaseRateTab />
          </TabPanel>

          {showDefaultInterestTab && (
            <TabPanel p={6} pt={4} bg="white" borderBottomRadius={20}>
              <DefaultInterestTab />
            </TabPanel>
          )}
          <TabPanel p={6} pt={4} bg="white" borderBottomRadius={20}>
            <StatementCreation />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};

export default PaymentsTab;
